import { z } from 'zod';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { PersistedEvent } from '@/chat/replay';

/**
 * A module run in progress, on the device.
 *
 * Deliberately its own store, its own `localStorage` key and its own version:
 * the profile store is the learner's identity and their scores, which must
 * survive everything, while a half-finished run is disposable and its encoding
 * will move as the chat does. Busting one should never bust the other.
 *
 * What is stored is the learner's *inputs*, not the thread they produced — see
 * `app/chat/replay.ts` for why, and for the positional encoding that lets the
 * same run resume in a different language.
 */

export const CHAT_PROGRESS_KEY = 'beach-passport-chat';

/** Bump to bust every saved run. Unknown versions are dropped, never migrated. */
const CHAT_PROGRESS_VERSION = 1;

/**
 * The run record's own version, inside the store's. It moves when the input
 * encoding changes but the store's shape does not, which lets a bad encoding
 * be dropped without throwing away the wrapper.
 */
const RUN_VERSION = 1;

const eventSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('option'), index: z.number().int().nonnegative() }),
  z.object({ kind: z.literal('options'), indexes: z.array(z.number().int().nonnegative()) }),
  z.object({ kind: z.literal('gate') }),
  z.object({ kind: z.literal('text'), value: z.string() }),
  z.object({
    kind: z.literal('activity'),
    correct: z.boolean(),
    receipt: z.string(),
    mistake: z.number().int().nonnegative().optional(),
  }),
  z.object({ kind: z.literal('reveal') }),
]) satisfies z.ZodType<PersistedEvent>;

const runSchema = z.object({
  version: z.literal(RUN_VERSION),
  /** The language the inputs were made in. Diagnostic: the replay never reads it. */
  language: z.string(),
  startedAt: z.number(),
  events: z.array(eventSchema),
  /**
   * How far the run had played, so the journey can draw a part-finished module
   * without loading its script and replaying it.
   *
   * Optional on purpose: it is a *reporting* field, derivable from the events
   * given the script, so a run saved before it existed stays resumable and
   * simply draws no bar. The replay never reads it.
   */
  progress: z.object({ stepsDone: z.number().int().nonnegative(), stepsTotal: z.number().int().positive() }).optional(),
});

const stateSchema = z.object({
  runs: z.record(z.string(), runSchema),
});

export type PersistedRun = z.infer<typeof runSchema>;

export type ChatProgressState = {
  /** Keyed by `module_key`, so a run is the same run in every language. */
  runs: Record<string, PersistedRun>;
};

/** The reporting half of a saved run: what the journey's bar is drawn from. */
export type RunProgress = { stepsDone: number; stepsTotal: number };

export type ChatProgressActions = {
  /**
   * Appends one learner input, opening a run for the module if there is none,
   * and records how far the run had played at that point.
   */
  record: (moduleKey: string, language: string, event: PersistedEvent, progress?: RunProgress) => void;
  /**
   * Updates how far an existing run has played.
   *
   * Never opens a run: a run is opened by a learner *input*, so merely mounting
   * a module does not make it "in progress". What this does do is keep the
   * saved position level with the module header's own rail — the chat reports
   * every step it plays, not just the answered ones — and populate `progress`
   * on a run saved before the field existed, the first time that module is
   * opened again.
   *
   * A no-op when nothing changed, so it does not churn `localStorage` on every
   * pacing tick.
   */
  saveProgress: (moduleKey: string, progress: RunProgress) => void;
  /**
   * Keeps only a run's first `count` inputs — the ones the script now loaded
   * could replay — so the learner's next input is appended after them rather
   * than after inputs that no longer fit.
   */
  keep: (moduleKey: string, count: number) => void;
  /** Forgets a module's run — it finished, or it no longer fits its script. */
  clear: (moduleKey: string) => void;
  clearAll: () => void;
};

export type ChatProgressStore = ChatProgressState & ChatProgressActions;

const emptyState: ChatProgressState = { runs: {} };

/**
 * Anything that comes back from storage, reduced to something the replay can
 * be handed. One bad run is enough to empty the store: a resumed run has to be
 * trustworthy, and starting a module over is a far smaller cost than crashing
 * on the way into it.
 */
export function parseChatProgress(persisted: unknown): ChatProgressState {
  const parsed = stateSchema.safeParse(persisted);

  return parsed.success ? { runs: parsed.data.runs } : emptyState;
}

/**
 * There is no migration path and there is not meant to be one: bumping
 * `CHAT_PROGRESS_VERSION` is how a bad encoding is busted, and the cost is that
 * anyone mid-module starts that module again.
 */
export function migrateChatProgress(_persisted: unknown, _version: number): ChatProgressState {
  return emptyState;
}

/** `localStorage` on the client, a no-op on the server. Same shim as the profile store. */
const storage = createJSONStorage<ChatProgressState>(() =>
  typeof window === 'undefined' ? { getItem: () => null, setItem: () => {}, removeItem: () => {} } : localStorage,
);

export const useChatProgressStore = create<ChatProgressStore>()(
  persist(
    (set) => ({
      ...emptyState,

      record: (moduleKey, language, event, progress) =>
        set((state) => {
          const run = state.runs[moduleKey];

          return {
            runs: {
              ...state.runs,
              [moduleKey]: run
                ? { ...run, events: [...run.events, event], progress: progress ?? run.progress }
                : { version: RUN_VERSION, language, startedAt: Date.now(), events: [event], progress },
            },
          };
        }),

      saveProgress: (moduleKey, progress) =>
        set((state) => {
          const run = state.runs[moduleKey];

          if (
            run === undefined ||
            (run.progress?.stepsDone === progress.stepsDone && run.progress?.stepsTotal === progress.stepsTotal)
          ) {
            return state;
          }

          return { runs: { ...state.runs, [moduleKey]: { ...run, progress } } };
        }),

      keep: (moduleKey, count) =>
        set((state) => {
          const run = state.runs[moduleKey];

          if (run === undefined || run.events.length <= count) {
            return state;
          }

          return { runs: { ...state.runs, [moduleKey]: { ...run, events: run.events.slice(0, count) } } };
        }),

      clear: (moduleKey) =>
        set((state) => {
          if (state.runs[moduleKey] === undefined) {
            return state;
          }

          const { [moduleKey]: _dropped, ...rest } = state.runs;

          return { runs: rest };
        }),

      clearAll: () => set(emptyState),
    }),
    {
      name: CHAT_PROGRESS_KEY,
      version: CHAT_PROGRESS_VERSION,
      storage,
      partialize: ({ runs }) => ({ runs }),
      migrate: migrateChatProgress,
      // The one place stored data becomes live state, so the one place worth
      // validating: `migrate` only runs on a version change, and a hand-edited
      // or half-written value at the current version would otherwise go
      // straight into the replay.
      merge: (persisted, current) => ({ ...current, ...parseChatProgress(persisted) }),
    },
  ),
);
